import { useMemo } from 'react';
import {
  Country,
  EntityType,
  TCRBrandIdentityStatus,
} from '@weave/schema-gen-ts/dist/schemas/tendlc/shared/v1/shared.pb';
import { TenCoordinatorV1 } from '@frontend/api-tendlc-coordinator';

export enum TCRBrandFriendlyRegistrationStatus {
  UNREGISTERED = 'Unregistered',
  PENDING = 'Pending',
  UNVERIFIED = 'Unverified',
  LOCKED = 'Locked',
  VERIFIED = 'Verified',
  SOLE_PROPRIETOR = 'Sole Proprietor',
  CANADA = 'Canada',
}

const PRIVATE_PROFIT_EXISTING_BRAND_IDENTITY_STATUS_MAP: Record<
  TCRBrandIdentityStatus,
  TCRBrandFriendlyRegistrationStatus
> = {
  [TCRBrandIdentityStatus.TCR_BRAND_IDENTITY_STATUS_SELF_DECLARED]: TCRBrandFriendlyRegistrationStatus.UNVERIFIED,
  [TCRBrandIdentityStatus.TCR_BRAND_IDENTITY_STATUS_UNSPECIFIED]: TCRBrandFriendlyRegistrationStatus.PENDING,
  [TCRBrandIdentityStatus.TCR_BRAND_IDENTITY_STATUS_UNVERIFIED]: TCRBrandFriendlyRegistrationStatus.UNVERIFIED,
  [TCRBrandIdentityStatus.TCR_BRAND_IDENTITY_STATUS_VERIFIED]: TCRBrandFriendlyRegistrationStatus.VERIFIED,
  [TCRBrandIdentityStatus.TCR_BRAND_IDENTITY_STATUS_VETTED_VERIFIED]: TCRBrandFriendlyRegistrationStatus.VERIFIED,
};

type GetFriendRegistrationStatusArgs = {
  entityType: EntityType;
  identityStatus: TCRBrandIdentityStatus;
  country: Country;
  locked: boolean;
};

export const getFriendlyRegistrationStatus = ({
  entityType,
  identityStatus,
  country,
  locked,
}: GetFriendRegistrationStatusArgs): TCRBrandFriendlyRegistrationStatus => {
  if (entityType === EntityType.ENTITY_TYPE_UNSPECIFIED && country === Country.COUNTRY_UNSPECIFIED) {
    return TCRBrandFriendlyRegistrationStatus.UNREGISTERED;
  }

  if (locked) {
    return TCRBrandFriendlyRegistrationStatus.LOCKED;
  }

  if (country === Country.COUNTRY_CA) {
    return TCRBrandFriendlyRegistrationStatus.CANADA;
  }

  if (entityType === EntityType.ENTITY_TYPE_SOLE_PROPRIETOR) {
    return TCRBrandFriendlyRegistrationStatus.SOLE_PROPRIETOR;
  }

  return PRIVATE_PROFIT_EXISTING_BRAND_IDENTITY_STATUS_MAP[identityStatus];
};

export const useTenDLCInfo = (locationId: string) => {
  const { data: tenDLCRegistrationBrand } = TenCoordinatorV1.Queries.useGetLocationsBrandComplianceQuery(
    {
      locationIds: locationId ? [locationId] : [],
    },
    {
      enabled: !!locationId,
      select: (data) => data.locationBrandCompliances[0],
    }
  );
  const { data: tenDLCTCRBrand } = TenCoordinatorV1.Queries.useGetLocationTCRBrandQuery(
    {
      locationId: locationId || '',
    },
    {
      enabled: !!locationId,
      select: (data) => data.tcrBrand,
    }
  );
  const { data: tenDLCRegistrationStatus } = TenCoordinatorV1.Queries.useGetLocationRegistrationStatusQuery(
    {
      locationId: locationId || '',
    },
    {
      enabled: !!locationId,
    }
  );
  const { data: tenDLCRegistrationStatusDetails } =
    TenCoordinatorV1.Queries.useGetLocationRegistrationStatusDetailsQuery(
      {
        locationId: locationId || '',
      },
      {
        enabled: !!locationId,
      }
    );
  const { data: tenDLCLockStatus } = TenCoordinatorV1.Queries.useGetTCRBrandLockStatusQuery(
    {
      brandId: tenDLCRegistrationBrand?.brandCompliance.brandId || '',
    },
    {
      enabled: !!locationId,
    }
  );

  const businessRegistrationStatus = useMemo(
    () =>
      getFriendlyRegistrationStatus({
        entityType: tenDLCTCRBrand?.entityType || EntityType.ENTITY_TYPE_UNSPECIFIED,
        identityStatus: tenDLCTCRBrand?.identityStatus || TCRBrandIdentityStatus.TCR_BRAND_IDENTITY_STATUS_UNSPECIFIED,
        country: tenDLCTCRBrand?.country || Country.COUNTRY_UNSPECIFIED,
        locked: tenDLCLockStatus?.locked || false,
      }),
    [tenDLCRegistrationStatusDetails, tenDLCTCRBrand, tenDLCLockStatus]
  );

  const phoneRegistrationStatus =
    businessRegistrationStatus === TCRBrandFriendlyRegistrationStatus.CANADA ||
    businessRegistrationStatus === TCRBrandFriendlyRegistrationStatus.SOLE_PROPRIETOR
      ? businessRegistrationStatus
      : tenDLCRegistrationStatus?.registered
      ? 'Registered'
      : 'Unregistered';

  const registrationNeedsAttention = !(
    businessRegistrationStatus === TCRBrandFriendlyRegistrationStatus.VERIFIED ||
    TCRBrandFriendlyRegistrationStatus.CANADA ||
    TCRBrandFriendlyRegistrationStatus.SOLE_PROPRIETOR
  );

  return {
    tenDLCRegistrationBrand,
    tenDLCRegistrationStatus,
    tenDLCRegistrationStatusDetails,
    tenDLCLockStatus,
    tenDLCTCRBrand,
    businessRegistrationStatus,
    registrationNeedsAttention,
    phoneRegistrationStatus,
  };
};
